import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { allowMonLogTypeDelete } from '../../graphql/monitoring-logs/allow-mon-log-type-delete';
import { createMonLog } from '../../graphql/monitoring-logs/create-mon-log';
import { createMonLogType } from '../../graphql/monitoring-logs/create-mon-log-type';
import { deleteMonLog } from '../../graphql/monitoring-logs/delete-mon-log';
import { deleteMonLogType } from '../../graphql/monitoring-logs/delete-mon-log-type';
import { emailMonLog } from '../../graphql/monitoring-logs/email-mon-log';
import { getCommFailSeverities } from '../../graphql/monitoring-logs/get-comm-fail-severities';
import { getGasReleaseTypeGrades } from '../../graphql/monitoring-logs/get-available-gas-release-type-grades';
import { queryForMonLogWorkTypes } from '../../graphql/monitoring-logs/query-for-mon-log-work-types';
import { getMonLogTypes } from '../../graphql/monitoring-logs/get-available-monitoring-types';
import { getMonLogWithAlarms } from '../../graphql/monitoring-logs/get-mon-log-with-alarms';
import { queryForActiveMonLogs } from '../../graphql/monitoring-logs/query-for-active-mon-logs';
import { queryForMonLogs } from '../../graphql/monitoring-logs/query-for-all-mon-logs';
import { queryForMonLogTypes } from '../../graphql/monitoring-logs/query-mon-log-types';
import { updateMonLog } from '../../graphql/monitoring-logs/update-mon-log';
import { updateMonLogType } from '../../graphql/monitoring-logs/update-mon-log-type';
import { MonLogTypeInput } from '../../model/admin/mon-log-type-input';
import { EmailRequest } from '../../model/admin/email-request';
import { BaseAppSyncService } from '../base-app-sync.service';
import { FacilityContactsForMonLog } from '../../model/monlogs/facility-contacts-for-mon-log';
import { getFacilityContactsForMonLog } from '../../graphql/monitoring-logs/get-facility-contacts-for-mon-log';
import { MonlogsBuilderService } from '../builder/monlogs-builder.service';
import { createMonLogEntry } from '../../graphql/monitoring-logs/create-mon-log-entry';
import {
  MonLogEntry,
  UpdateMonLogEntryInput,
} from '../../model/monlogs/mon-log-entry';
import { updateMonLogEntry } from '../../graphql/monitoring-logs/update-mon-log-entry';
import { deleteMonLogEntry } from '../../graphql/monitoring-logs/delete-mon-log-entry';
import { createMonLogAlarm } from '../../graphql/monitoring-logs/create-mon-log-alarm';
import {
  MonLogAlarm,
  UpdateMonLogAlarmInput,
} from '../../model/monlogs/mon-log-alarm';
import { updateMonLogAlarm } from '../../graphql/monitoring-logs/update-mon-log-alarm';
import { deleteMonLogAlarm } from '../../graphql/monitoring-logs/delete-mon-log-alarm';
import {
  ControlRoomEventReviewEntry,
  ControlRoomEventReviewEntryInput,
} from '../../model/monlogs/control-room-event-review-entry';
import { createCREREntry } from '../../graphql/monitoring-logs/create-crer-entry';
import { updateCREREntry } from '../../graphql/monitoring-logs/update-crer-entry';
import { deleteCREREntry } from '../../graphql/monitoring-logs/delete-crer-entry';
import { MonLog } from '../../model/monlogs/mon-log';
import { MonLogType } from 'src/app/model/admin/mon-log-type';
import {
  ActiveMonLogConnection,
  ActiveMonLogSortOrQuery,
  MonLogConnection,
  MonLogSortOrQuery,
} from 'src/app/model/monlogs/mon-log-search-result';
import { ConfigItem } from 'src/app/model/common/config-item';

@Injectable({
  providedIn: 'root',
})
export class MonlogsApiService extends BaseAppSyncService {
  private monLogBuilder = inject(MonlogsBuilderService);

  // Grab all Monitoring Log types
  // Set to arbitrarily large value to avoid pagination / filtering
  // Default sort is on name, descending
  // need to pass null for business units so the backend function will filter based on user roles
  getMonLogTypes(): Observable<any[]> {
    return this.query(
      getMonLogTypes,
      {
        limit: 9999999,
        page: 1,
        query: {
          name: '',
          enabled: 'true',
          businessUnits: null,
          requiresCRER: null,
        },
        sort: {
          name: 'DESC',
        },
      },
      (i: any) => i.items,
      'queryForMonLogTypes',
      'cache-first'
    );
  }

  getGasReleaseTypeGrades(): Observable<ConfigItem[]> {
    return this.query(
      getGasReleaseTypeGrades,
      {},
      (items: any[]) => items,
      'getGasReleaseTypeGrades',
      'cache-first'
    );
  }

  getCommFailSeverities(): Observable<ConfigItem[]> {
    return this.query(
      getCommFailSeverities,
      {},
      (i: any[]) => i,
      'getCommFailSeverities',
      'cache-first'
    );
  }

  queryForMonLogWorkTypes(): Observable<any> {
    return this.query(
      queryForMonLogWorkTypes,
      {
        limit: 9999999,
        page: 1,
        query: {
          name: '',
          enabled: true,
        },
        sort: {
          name: 'DESC',
        },
      },
      (i: any) => i,
      'queryForMonLogWorkTypes',
      'cache-first'
    );
  }

  queryForMonLogs(
    limit: number,
    page: number,
    query: MonLogSortOrQuery,
    sort: MonLogSortOrQuery
  ): Observable<MonLogConnection> {
    return this.query(
      queryForMonLogs,
      { limit, page, query, sort },
      (i: any) => this.monLogBuilder.buildMonLogConnection(i),
      'queryForMonLogs'
    );
  }

  queryForActiveMonLogs(
    limit: number,
    page: number,
    query: ActiveMonLogSortOrQuery,
    sort: ActiveMonLogSortOrQuery,
    filter: string[]
  ): Observable<ActiveMonLogConnection> {
    return this.query(
      queryForActiveMonLogs,
      { limit, page, query, sort, filter },
      (i: any) => this.monLogBuilder.buildActiveMonLogConnection(i),
      'queryForActiveMonLogs'
    );
  }

  createMonLog(logInput: any): Observable<MonLog> {
    return this.mutate(
      createMonLog,
      { input: logInput },
      (i: any) => this.monLogBuilder.buildMonLog(i),
      'createMonLog'
    );
  }

  updateMonLog(log: MonLog, logInput: any): Observable<MonLog> {
    return this.mutate(
      updateMonLog,
      { id: log.id, input: logInput },
      (i: any) => this.monLogBuilder.buildMonLog(i),
      'updateMonLog'
    );
  }

  deleteMonLog(id: string): Observable<boolean> {
    return this.mutate(
      deleteMonLog,
      { id },
      (i: any) => i.value,
      'deleteMonLog'
    );
  }

  emailMonLog(request: EmailRequest): Observable<any> {
    return this.mutate(
      emailMonLog,
      { input: request },
      (i: any) => i,
      'emailMonLog'
    );
  }

  getMonLogWithAlarms(id: string): Observable<MonLog> {
    return this.query(
      getMonLogWithAlarms,
      { id },
      (item: any) => this.monLogBuilder.buildMonLog(item),
      'getMonLogWithAlarms'
    );
  }

  queryMonLogTypes(
    limit: number,
    page: number,
    query: any,
    sort: any
  ): Observable<any> {
    return this.query(
      queryForMonLogTypes,
      { limit, page, query, sort },
      (i: any) => i,
      'queryForMonLogTypes'
    );
  }

  createMonLogType(input: MonLogTypeInput | null): Observable<MonLogType> {
    return this.mutate(
      createMonLogType,
      { input },
      (i: any) => i,
      'createMonLogType'
    );
  }

  updateMonLogType(
    id: string,
    changes: MonLogTypeInput | null
  ): Observable<MonLogType> {
    return this.mutate(
      updateMonLogType,
      { id, changes },
      (i: any) => i,
      'updateMonLogType'
    );
  }

  deleteMonLogType(id: string): Observable<boolean> {
    return this.mutate(
      deleteMonLogType,
      { id },
      (i: any) => i,
      'deleteMonLogType'
    );
  }

  allowMonLogTypeDelete(id: string): Observable<boolean> {
    return this.query(
      allowMonLogTypeDelete,
      { id },
      (i: any) => i.value,
      'allowMonLogTypeDelete'
    );
  }

  getFacilityContactsForMonLog(
    facilityId: string
  ): Observable<FacilityContactsForMonLog> {
    return this.query(
      getFacilityContactsForMonLog,
      { facilityId },
      (i: any) => this.monLogBuilder.buildFacilityContactsForMonLog(i),
      'getFacilityContactsForMonLog'
    );
  }

  createMonLogEntry(
    monLogId: string,
    input: UpdateMonLogEntryInput
  ): Observable<MonLogEntry> {
    return this.mutate(
      createMonLogEntry,
      { monLogId, input },
      (i: any) => this.monLogBuilder.buildMonLogEntry(i),
      'createMonLogEntry'
    );
  }

  updateMonLogEntry(
    id: string,
    input: UpdateMonLogEntryInput
  ): Observable<MonLogEntry> {
    return this.mutate(
      updateMonLogEntry,
      { id, input },
      (i: any) => this.monLogBuilder.buildMonLogEntry(i),
      'updateMonLogEntry'
    );
  }

  deleteMonLogEntry(id: string): Observable<boolean> {
    return this.mutate(
      deleteMonLogEntry,
      { id },
      (i: any) => i.value,
      'deleteMonLogEntry'
    );
  }

  createMonLogAlarm(
    monLogId: string,
    input: UpdateMonLogAlarmInput
  ): Observable<MonLogAlarm> {
    return this.mutate(
      createMonLogAlarm,
      { monLogId, input },
      (i: any) => this.monLogBuilder.buildMonLogAlarm(i),
      'createMonLogAlarm'
    );
  }

  updateMonLogAlarm(
    id: string,
    input: UpdateMonLogAlarmInput
  ): Observable<MonLogAlarm> {
    return this.mutate(
      updateMonLogAlarm,
      { id, input },
      (i: any) => this.monLogBuilder.buildMonLogAlarm(i),
      'updateMonLogAlarm'
    );
  }

  deleteMonLogAlarm(id: string): Observable<boolean> {
    return this.mutate(
      deleteMonLogAlarm,
      { id },
      (i: any) => i.value,
      'deleteMonLogAlarm'
    );
  }

  createCREREntry(
    monLogId: string,
    input: ControlRoomEventReviewEntryInput
  ): Observable<ControlRoomEventReviewEntry> {
    return this.mutate(
      createCREREntry,
      { monLogId, input },
      (i: any) => this.monLogBuilder.buildCREREntry(i),
      'createCREREntry'
    );
  }

  updateCREREntry(
    id: string,
    input: ControlRoomEventReviewEntryInput
  ): Observable<ControlRoomEventReviewEntry> {
    return this.mutate(
      updateCREREntry,
      { id, input },
      (i: any) => this.monLogBuilder.buildCREREntry(i),
      'updateCREREntry'
    );
  }

  deleteCREREntry(id: string): Observable<boolean> {
    return this.mutate(
      deleteCREREntry,
      { id },
      (i: any) => i.value,
      'deleteCREREntry'
    );
  }
}
